import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError, tokenStore } from "../lib/api";
import { BUILD_TAG } from "../lib/buildTag";
import { Header } from "../components/Header";
import { SmartMap } from "../components/SmartMap";
import { PlaceAutocomplete } from "../components/PlaceAutocomplete";
import { ActiveRideCard } from "../components/driver/ActiveRideCard";
import { NavigationFullScreen } from "../components/driver/NavigationFullScreen";
import { CompletedRideCard } from "../components/driver/CompletedRideCard";
import { EarningsTodayLink, MembershipLink, RecentTripsList } from "../components/driver/EmptyStates";
import { DriverDashboardPanel } from "../components/driver/DriverDashboardPanel";
import { DriverDocumentsPanel } from "../components/driver/DriverDocumentsPanel";
import { DocumentExpiryBanner } from "../components/driver/DocumentExpiryBanner";
import { DriverPaymentPanel } from "../components/driver/DriverPaymentPanel";
import { DriverOnboardingChecklist } from "../components/driver/DriverOnboardingChecklist";
import { DriverCommPrefs } from "../components/driver/DriverCommPrefs";
import { DriverEmergencyContacts } from "../components/DriverEmergencyContacts";
import { DriverCockpit } from "../components/driver/DriverCockpit";
import { DriverFinancialPrivacyProvider } from "../lib/driverFinancialPrivacy";
import { DriverEarningsGate, DriverPinSettings } from "../components/driver/DriverEarningsPrivacy";
import { useRealtime } from "../hooks/useRealtime";
import { useOfferRingtone } from "../hooks/useOfferRingtone";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Star, Clock, AlertTriangle, Ban, X } from "lucide-react";
import { RideChatDialog } from "../components/RideChatDialog";
import { SosButton } from "../components/SosButton";

// Auto-arrival threshold (kilometres). 100m is the sweet spot: tight enough
// not to fire mid-journey, lenient enough to survive ±20m consumer GPS jitter
// and the inevitable "park around the corner" scenario.
const ARRIVAL_THRESHOLD_KM = 0.1;

// Pre-arrival heads-up threshold. ~500m ≈ 1 to 2 minutes at urban speed —
// long enough for the passenger to grab their bag and shoes before the
// driver actually pulls up.
const NEARBY_THRESHOLD_KM = 0.5;

// Haversine — great-circle distance in km between two lat/lng points.
// Kept inline (no util import) to avoid pulling a new module just for one fn.
const haversineKm = (lat1, lng1, lat2, lng2) => {
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return Infinity;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
};

const PRESET_DRIVER_LOCATIONS = [
  { name: "Town Centre", lat: 51.2664, lng: -1.0879 },
  { name: "Brighton Hill", lat: 51.2438, lng: -1.0941 },
  { name: "Chineham", lat: 51.2941, lng: -1.0524 },
  { name: "Old Basing", lat: 51.2752, lng: -1.0429 },
  { name: "Popley", lat: 51.2841, lng: -1.0856 },
  { name: "Hatch Warren", lat: 51.2392, lng: -1.1116 },
];

// Format a millisecond span as a compact "Xh Ym" (or "Ym" under an hour).
const fmtOnlineSpan = (ms) => {
  if (!ms || ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};
// Format decimal hours (from the fatigue calc) as "Xh Ym".
const fmtDecimalHours = (hrs) => {
  if (hrs == null || isNaN(hrs)) return null;
  const total = Math.round(hrs * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};


export default function Driver() {
  const { user, loading, refresh, logout } = useAuth();
  const navigate = useNavigate();

  const [isOnline, setIsOnline] = useState(false);
  // Read-only "online time" display. `onlineSince` is the authoritative session
  // start from the backend (set_driver_online_state); `todayOnlineHours` reuses
  // the existing fatigue calc. `nowTick` re-renders the live "Online for" label.
  const [onlineSince, setOnlineSince] = useState(null);
  const [todayOnlineHours, setTodayOnlineHours] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  // Connection-stale flag: backend's janitor flags drivers whose last
  // heartbeat is older than DRIVER_INACTIVITY_SECONDS. The flag is cleared
  // automatically on the next fresh /driver/location heartbeat. We render
  // a banner so the driver knows to keep this screen open / re-grant GPS.
  // POLICY: drivers are NEVER auto-offlined for idle time anymore —
  // they only see this warning. They remain dispatch-eligible until they
  // explicitly tap Offline.
  const [connectionStale, setConnectionStale] = useState(false);
  // Ringtone preference — persisted across sessions in localStorage so a
  // driver who has muted on their day off doesn't get blasted next shift.
  // Default = enabled (matches the user spec).
  const [ringtoneMuted, setRingtoneMuted] = useState(() => {
    try {
      return localStorage.getItem("driver_ringtone_muted") === "1";
    } catch { return false; }
  });
  // Audio alerts are ON by default (see `ringtoneMuted` above). The audio
  // session is unlocked SILENTLY on the driver's first tap/keydown via the
  // document-level listener inside `useOfferRingtone` — no "enable sound"
  // prompt is shown. The driver mutes/unmutes via the speaker toggle, and
  // that preference persists per device (`driver_ringtone_muted`).
  const [currentLoc, setCurrentLoc] = useState(PRESET_DRIVER_LOCATIONS[0]);
  const [pendingOffer, setPendingOffer] = useState(null);
  const [activeRide, setActiveRide] = useState(null);
  const [rides, setRides] = useState([]);
  const [myRating, setMyRating] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [navApp, setNavApp] = useState(() => {
    try { return localStorage.getItem("driver_nav_app") || "google"; }
    catch { return "google"; }
  });
  // Manual override flags — driver can tap "I've arrived" if GPS hasn't yet
  // dropped them inside the 100m geofence. Keyed by ride id so we forget the
  // override the moment a new ride starts.
  const [manualArrivedPickup, setManualArrivedPickup] = useState(false);
  const [manualArrivedDropoff, setManualArrivedDropoff] = useState(false);
  // In-app confirmation for manual arrival when the driver is outside the
  // pickup geofence (or GPS is blocked). Native window.confirm() is silently
  // suppressed inside iOS standalone PWAs / in-app webviews, which dropped the
  // manual-arrival tap and left phone-booked rides stuck at ASSIGNED.
  const [manualArrivePromptOpen, setManualArrivePromptOpen] = useState(false);
  // Decoded polyline from /api/driver/directions/{rideId} — passed down to
  // SmartMap as a route overlay so the driver sees the actual driving route
  // (not just a straight pickup→dropoff line). Reset whenever activeRide
  // unmounts to avoid stale routes flashing during the next ride.
  const [directionsRoute, setDirectionsRoute] = useState(null);
  const [navInfo, setNavInfo] = useState(null);
  const [navMapOpen, setNavMapOpen] = useState(false);
  // Real device GPS state. `available` is tri-state:
  //   null  → not yet attempted (or geolocation not requested yet)
  //   true  → we've received at least one fix this online session
  //   false → permission denied / unsupported / timeout
  // `lastFixAt` is the wall-clock ms of the most recent successful fix so
  //   we can surface "stale GPS" warnings (>30s without an update).
  const [gpsStatus, setGpsStatus] = useState({
    available: null, lastFixAt: null, accuracy: null, error: null, code: null,
  });
  // Latest device heading/course (degrees from true north) from GPS. Kept as
  // the last finite reading so the in-app navigator can offer Head Up mode;
  // it stays null until the device reports a real heading (often only while
  // moving). UI-only — never affects dispatch/lifecycle/fare logic.
  const [gpsHeading, setGpsHeading] = useState(null);
  // Latest GPS ground speed in mph (null when the device reports no speed).
  const [gpsSpeedMph, setGpsSpeedMph] = useState(null);
  // Wall-clock ms of the last successful /driver/location heartbeat POST —
  // surfaced in the navigation debug overlay so we can prove heartbeat vs GPS
  // independence on a real device.
  const [lastHeartbeatAt, setLastHeartbeatAt] = useState(null);
  // Bumped to force an immediate directions re-fetch when the navigator
  // detects the driver is off-route (auto-rerouting). Display/UX only.
  const [rerouteSignal, setRerouteSignal] = useState(0);
  const pollRef = useRef(null);
  const locStreamRef = useRef(null);
  const offerCardRef = useRef(null);
  const lastOfferIdRef = useRef(null);
  const lastArrivalToastRef = useRef({ ride: null, phase: null });
  // navigator.geolocation.watchPosition handle so we can clear it on offline.
  const gpsWatchIdRef = useRef(null);

  // ── Screen Wake Lock ────────────────────────────────────────────────────
  // Keep the driver's screen awake during an ACTIVE trip / in-app navigation
  // so the phone never sleeps mid-route. The OS releases the lock whenever the
  // tab is backgrounded / the device locks, so we re-acquire on visibility,
  // focus, pageshow and app-resume. Released the moment the trip/nav ends.
  // Strictly UI/UX — touches NO dispatch, lifecycle, offer-routing, janitor,
  // fare or payment logic.
  const wakeLockRef = useRef(null);
  const wakeLockUnsupportedNotedRef = useRef(false);
  useEffect(() => {
    // "Active" = the driver has a live ride OR the full-screen navigator is open.
    const shouldHold = !!activeRide || navMapOpen;
    const supported = typeof navigator !== "undefined" && "wakeLock" in navigator;

    const release = async () => {
      try {
        if (wakeLockRef.current) await wakeLockRef.current.release();
      } catch { /* already released / page hidden — ignore */ }
      wakeLockRef.current = null;
    };

    const acquire = async () => {
      if (!shouldHold || wakeLockRef.current) return;
      if (!supported) {
        // One-time, non-blocking heads-up. The trip still works; the screen
        // just follows the device's normal auto-lock timeout.
        if (!wakeLockUnsupportedNotedRef.current) {
          wakeLockUnsupportedNotedRef.current = true;
          toast.message("Screen may dim during the trip", {
            description: "This browser can't keep the screen awake — adjust your phone's auto-lock if needed.",
          });
        }
        return;
      }
      try {
        const lock = await navigator.wakeLock.request("screen");
        wakeLockRef.current = lock;
        // The OS auto-releases on background — clear our handle so the
        // foreground handlers below know to re-acquire.
        lock.addEventListener("release", () => { wakeLockRef.current = null; });
      } catch { /* power-save / no user gesture may reject — non-fatal */ }
    };

    if (shouldHold) {
      acquire();
      const reacquire = () => {
        if (document.visibilityState === "visible") acquire();
      };
      document.addEventListener("visibilitychange", reacquire);
      window.addEventListener("focus", reacquire);
      window.addEventListener("pageshow", reacquire);
      window.addEventListener("resume", reacquire); // WebView / Cordova app-resume
      return () => {
        document.removeEventListener("visibilitychange", reacquire);
        window.removeEventListener("focus", reacquire);
        window.removeEventListener("pageshow", reacquire);
        window.removeEventListener("resume", reacquire);
        release();
      };
    }

    // Not active → ensure any held lock is released.
    release();
    return undefined;
  }, [activeRide, navMapOpen]);


  // Per-ride view toggle during trip_in_progress: "nav" (default) shows the
  // DirectionsPanel, "meter" shows the FareMeterPanel. Stored per-ride so
  // switching tabs doesn't survive across rides. Persisted to sessionStorage
  // so a tab reload (or bfcache restore) doesn't kick the driver back to the
  // NAV view mid-trip.
  const [tripView, setTripView] = useState(() => {
    try { return sessionStorage.getItem("driver_trip_view") || "nav"; } catch { return "nav"; }
  });
  const setTripViewPersisted = (v) => {
    setTripView(v);
    try { sessionStorage.setItem("driver_trip_view", v); } catch { /* ignore */ }
  };
  // Post-completion confirmation card. Stays visible until the driver
  // dismisses it (or accepts the next ride). Spec: "dismissible post-
  // completion fare breakdown card".
  const [lastCompleted, setLastCompleted] = useState(null);
  // Full-screen blocking acknowledgement shown when a ride the driver had
  // ACCEPTED is cancelled out from under them (passenger or dispatch). A
  // toast is too easy to miss when the driver is already en route to the
  // pickup, so we force an explicit "Got It" acknowledgement + alert sound.
  // Shape: { by: "passenger" | "admin" } | null.
  const [cancelledNotice, setCancelledNotice] = useState(null);
  // Tracks which ride we've already POSTed /driver/nearby for (the 500m
  // pre-arrival heads-up) to keep us from spamming the passenger.
  const nearbyNotifiedRef = useRef(null);

  // Ride-scoped chat (Phase 1 templated messages). `chatOpen` is local UI
  // state; `incomingMessage` is bumped on every WS `ride_message` event
  // so RideChatDialog re-fetches when open.
  const [chatOpen, setChatOpen] = useState(false);
  const [incomingMessage, setIncomingMessage] = useState(null);
  // SOS chat — WS-driven refresh signal (same pattern as `incomingMessage`).
  const [sosMsgSignal, setSosMsgSignal] = useState(null);
  // Map-first cockpit — active bottom-nav tab: home | earnings | trips | account.
  const [cockpitTab, setCockpitTab] = useState("home");
  // { type, n } — a fresh object per Renew tap so the same document can be reopened.
  const [renewDocType, setRenewDocType] = useState(null);
  // Deep link from the Compliance Hold email: /driver?renew=<doc_type> (kept through sign-in).
  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("renew") || sessionStorage.getItem("driver_renew_doc");
    sessionStorage.removeItem("driver_renew_doc");
    if (t) { setRenewDocType({ type: t, n: Date.now() }); setCockpitTab("account"); }
  }, []);

  // Play a short beep through the Web Audio API. No external file → works
  // offline, no asset shipping. Falls through silently in unsupported browsers.
  const playOfferChime = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(660, ctx.currentTime + 0.18);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.55);
    } catch { /* ignore — audio is best-effort */ }
  };

  // Raise the full-screen cancellation acknowledgement + alert chime.
  // Deduped per ride id so BOTH trigger paths can fire safely without
  // double-chiming:
  //   1. the realtime `ride_cancelled` WS frame (fast path), and
  //   2. the /driver/my-rides poll fallback in fetchAll() (resilient path
  //      for when the WS frame is missed because the socket was dropped).
  // `by` comes from the WS frame (`msg.by`) or the poll row
  // (`cancelled_by`): "admin" → dispatch wording, anything else → passenger.
  const cancelNotifiedRef = useRef(null);
  const raiseCancelledModal = (rideId, by) => {
    if (!rideId || cancelNotifiedRef.current === rideId) return;
    cancelNotifiedRef.current = rideId;
    playOfferChime();
    setCancelledNotice({ by: by === "admin" ? "admin" : "passenger" });
  };

  // Fire haptic + chime + auto-scroll when a NEW offer lands. Prevents drivers
  // from missing the offer when their phone is in a pocket or the offer card
  // is below the fold on a small screen.
  useEffect(() => {
    const currentId = pendingOffer?.id || null;
    if (currentId && currentId !== lastOfferIdRef.current) {
      playOfferChime();
      if (navigator.vibrate) navigator.vibrate([220, 110, 220, 110, 220]);
      // Scroll the offer card into view shortly after render
      setTimeout(() => {
        offerCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
    lastOfferIdRef.current = currentId;
  }, [pendingOffer?.id]);

  const selectNavApp = (app) => {
    setNavApp(app);
    try { localStorage.setItem("driver_nav_app", app); } catch { /* iOS Private — ignore */ }
  };

  // Navigate buttons render as anchors with target="_blank" — see active-ride
  // panel below. This bypasses the Emergent App Preview's iframe popup blocker
  // that otherwise triggers "ERR_BLOCKED_BY_RESPONSE / google.com is blocked".



  // WebSocket push - instantly fetch when offer arrives.
  // Defence (iteration 57): if the event carries an explicit recipient,
  // verify it matches the currently authenticated user. Today the
  // backend only addresses ride_offer to a single driver via
  // manager.send(driver_id, ...), so this guard is defensive only —
  // but it pairs cleanly with the same check in fetchAll() against
  // localStorage-shared sessions.
  useRealtime(tokenStore.get(), (msg) => {
    if (["ride_offer", "ride_cancelled", "offer_expired"].includes(msg.type)) {
      // If the message carries a `driver_id`/`offered_to` and it does
      // not match the current authenticated user, ignore. fetchAll will
      // still run on a different tick if the server later confirms an
      // offer belongs to us.
      const recipient = msg.driver_id || msg.offered_to;
      if (recipient && user && recipient !== user.id) {
        // eslint-disable-next-line no-console
        console.warn(
          "[driver] REJECTED ws event — recipient does not match logged-in user.",
          { msgType: msg.type, recipient, currentUserId: user.id }
        );
        return;
      }
      fetchAll();
      if (msg.type === "ride_offer") {
        toast.success("New ride offer arrived");
        // The repeating ringtone is now driven by `useOfferRingtone`
        // below — it kicks in when `pendingOffer` flips truthy on the
        // next fetchAll tick, and stops automatically when it flips
        // back. We deliberately don't play a one-shot here anymore so
        // the two audio sources can't stack and overlap.
      }
    }
    // Passenger (or dispatch) cancelled a ride the driver had already
    // ACCEPTED. The generic block above already refetches and clears
    // state, but a silent clear is dangerous when the driver is en route
    // to (or parked at) the pickup — they could keep waiting for a no-show.
    // So when the cancelled ride matches the driver's current activeRide we
    // raise a full-screen blocking modal + alert chime that must be
    // dismissed. NOTE: this WS frame is fire-and-forget — if the driver's
    // socket is backgrounded/dropped (very common while parked at pickup)
    // the frame is lost. The /driver/my-rides poll fallback in fetchAll()
    // is the safety net that raises the SAME modal in that case.
    if (msg.type === "ride_cancelled") {
      if (activeRide && (!msg.ride_id || activeRide.id === msg.ride_id)) {
        setActiveRide(null);
        setDirectionsRoute(null);
        raiseCancelledModal(activeRide.id, msg.by);
      }
    }
    // Ride-scoped templated message arriving from the counterparty.
    // We surface a non-intrusive toast and bump `incomingMessage` so an
    // already-open RideChatDialog re-fetches; the thread is the source
    // of truth so we don't try to splice the bubble in client-side.
    if (msg.type === "ride_message") {
      const isSelf = msg.from_user_id && user && msg.from_user_id === user.id;
      if (!isSelf) {
        if (msg.via === "sms") {
          // Inbound reply from a phone-booked customer (SMS bridge).
          toast.message("Customer replied by SMS", {
            description: msg.body,
          });
        } else {
          toast.message(`${msg.from_name || "Passenger"}: ${msg.body}`, {
            description: "Tap MESSAGE on the ride card to reply.",
          });
        }
      }
      setIncomingMessage(msg);
    }
    // SOS in-app messaging — mirror the ride_message pattern: bump a signal so
    // the SosButton thread re-fetches (the GET thread is the source of truth),
    // and surface a non-intrusive toast. Isolated to the Safety/SOS module.
    if (msg.type === "sos_message") {
      setSosMsgSignal({ ts: Date.now(), body: msg.body });
      toast.message(`Control Room: ${msg.body || ""}`, { description: "Open SOS chat to reply." });
    }
    // Connection-stale signal from janitor — render the banner without
    // waiting for the next /auth/me refresh. Cleared the moment the next
    // /driver/location heartbeat lands (see backend $unset on the user doc).
    if (msg.type === "connection_stale") {
      setConnectionStale(true);
    }
    // Server took the driver offline: a mandatory document expired or was invalidated.
    if (msg.type === "compliance_offline") {
      setIsOnline(false);
      setOnlineSince(null);
      toast.error(msg.message || "You've been taken offline: a required document needs attention.", { duration: 12000 });
    }
    // Admin force-completed the ride. Driver did not press End Trip
    // themselves (because the geofence blocked them), so `lastCompleted`
    // was never set by handleEndTrip. The backend embeds the full ride
    // payload in the WS event so we can populate the receipt
    // SYNCHRONOUSLY — without this, an API round-trip to /driver/my-rides
    // races against the activeRide poll and the UI clears before the
    // receipt renders.
    if (msg.type === "ride_completed") {
      const ride = msg.ride;
      if (ride && ride.id) {
        // CRITICAL: clear activeRide synchronously so the render
        // condition `!activeRide && lastCompleted` becomes true on the
        // SAME render that sets lastCompleted. Without this, the
        // receipt is gated behind the next /driver/active-ride poll
        // (5-15s), during which the driver still sees the dropoff
        // screen and gets repeated geofence errors when they tap End
        // Trip. Production bug 2026-06-06: confirmed in 4th iteration.
        setActiveRide((prev) => (prev && prev.id === ride.id ? null : prev));
        // Also clear any stale pending offer pointing at this ride.
        setPendingOffer((prev) => (prev && prev.id === ride.id ? null : prev));
        setLastCompleted({
          id: ride.id,
          passenger_name: ride.passenger_name,
          pickup_address: ride.pickup_address,
          dropoff_address: ride.dropoff_address,
          fare: ride.fare,
          payment_status: ride.payment_status || "pending",
          payment_method: ride.payment_method || "cash",
          breakdown: ride.fare_breakdown || ride.breakdown,
          completed_at: ride.completed_at || new Date().toISOString(),
          completed_by: ride.completed_by || msg.by || "admin",
        });
        if (msg.by === "admin") {
          toast.message("Ride force-completed by admin", {
            description: (ride.payment_method || "cash") === "cash"
              ? "Collect cash from passenger and tap Got It."
              : "Passenger will pay by card.",
          });
        }
      }
    }
    // Stripe webhook → backend → WS push. Flip the just-completed
    // receipt card from PENDING → PAID without needing a manual refresh.
    // We update BOTH lastCompleted (the on-screen receipt) and the
    // rides[] history list so DriverEarnings stays consistent.
    if (msg.type === "ride_payment_received") {
      setLastCompleted((prev) =>
        prev && prev.id === msg.ride_id
          ? { ...prev, payment_status: "paid", paid_at: msg.paid_at }
          : prev,
      );
      setRides((prev) =>
        Array.isArray(prev)
          ? prev.map((r) =>
              r.id === msg.ride_id
                ? { ...r, payment_status: "paid", paid_at: msg.paid_at }
                : r,
            )
          : prev,
      );
      toast.success(
        msg.fare ? `Card payment received · £${msg.fare}` : "Card payment received",
      );
    }
  });

  // Safety-net polling for the just-completed CARD receipt. The primary
  // path is the `ride_payment_received` WebSocket push (above) — but if
  // the WS happens to be disconnected when the Stripe webhook lands
  // (driver's phone backgrounded, signal blip, app suspended), poll the
  // backend every 4s until the ride flips to paid OR the driver
  // dismisses the card. Stops automatically when payment_status === "paid"
  // or lastCompleted goes null. Cash rides skip the poll — they settle
  // via the explicit `markCashCollected` button.
  useEffect(() => {
    if (!lastCompleted?.id) return;
    if (lastCompleted.payment_method !== "card") return;
    if (lastCompleted.payment_status === "paid") return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/driver/my-rides`);
        const updated = (data || []).find((r) => r.id === lastCompleted.id);
        if (updated && updated.payment_status === "paid" && !cancelled) {
          setLastCompleted((prev) =>
            prev && prev.id === updated.id
              ? { ...prev, payment_status: "paid", paid_at: updated.paid_at }
              : prev,
          );
          toast.success(
            updated.fare
              ? `Card payment received · £${updated.fare}`
              : "Card payment received",
          );
        }
      } catch { /* swallow — next tick will retry */ }
    };
    const interval = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [lastCompleted?.id, lastCompleted?.payment_method, lastCompleted?.payment_status]);

  // Safety net for admin force-complete when the WS event is missed
  // (driver backgrounded, signal blip). We track the last activeRide id
  // we saw; if it disappears AND lastCompleted is still null, we treat
  // that as a candidate "completed elsewhere" event and fetch
  // /driver/my-rides to populate the receipt. Without this, a driver
  // whose phone slept through the admin force-complete WS message would
  // never see the cash-collection or card-pending receipt card.
  const lastActiveRideIdRef = useRef(null);
  useEffect(() => {
    if (activeRide?.id) {
      lastActiveRideIdRef.current = activeRide.id;
      return;
    }
    // activeRide just cleared. Was there a prior one we already showed
    // a receipt for? If yes, nothing to do.
    const priorId = lastActiveRideIdRef.current;
    if (!priorId) return;
    if (lastCompleted && lastCompleted.id === priorId) {
      lastActiveRideIdRef.current = null;
      return;
    }
    // ActiveRide vanished but no receipt — admin probably force-completed.
    // Fetch the canonical record and populate lastCompleted ourselves.
    (async () => {
      try {
        const { data: myRides } = await api.get("/driver/my-rides");
        const ride = (myRides || []).find((r) => r.id === priorId && r.status === "completed");
        if (!ride) return;
        setLastCompleted({
          id: ride.id,
          passenger_name: ride.passenger_name,
          pickup_address: ride.pickup_address,
          dropoff_address: ride.dropoff_address,
          fare: ride.fare,
          payment_status: ride.payment_status,
          payment_method: ride.payment_method,
          breakdown: ride.fare_breakdown || ride.breakdown,
          completed_at: ride.completed_at || new Date().toISOString(),
          completed_by: ride.completed_by || "admin",
        });
        toast.message("Ride completed by admin", {
          description: ride.payment_method === "cash"
            ? "Collect cash from passenger and tap Got It."
            : "Passenger will pay by card.",
        });
        lastActiveRideIdRef.current = null;
      } catch { /* next render will retry if applicable */ }
    })();
  }, [activeRide?.id, lastCompleted?.id]);






  // Repeating ringtone driven solely by `pendingOffer.id` — keying on
  // the id (not boolean truthiness) means a rotation that lands a
  // different offer on this same tab also retriggers, and every path
  // that clears `pendingOffer` (accept, decline, expire, reassign,
  // offline-gate, cross-driver reject) stops the chime via the same
  // React state. No extra wiring needed in the action handlers.
  const { unlock: unlockRingtone, testBeep } = useOfferRingtone({
    activeOfferId: pendingOffer?.id || null,
    muted: ringtoneMuted,
  });

  // The AudioContext / ringtone element is unlocked SILENTLY on the driver's
  // first interaction (document pointerdown/keydown listener in the hook) and
  // again on the Online toggle + mute toggle taps — so no "enable sound"
  // prompt is ever shown. `unlockRingtone` is safe to call repeatedly.

  // When the driver returns from a backgrounded tab (iOS Safari
  // aggressively suspends background tabs — JS timers paused, WS
  // messages queued), force an immediate fetchAll so stale activeRide
  // state from before backgrounding is reconciled with server truth.
  // Without this, drivers who switch to Google Maps during a trip and
  // come back after admin force-completes see the stale dropoff
  // screen and tap End Trip → hit the local geofence check on stale
  // state → get the misleading "you're 2392m from dropoff" error.
  const fetchAllRef = useRef(null);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && fetchAllRef.current) {
        fetchAllRef.current();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);


  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
    } else if (user.role !== "driver") {
      // Token belongs to a different role — send them to where they belong.
      navigate(user.role === "admin" ? "/admin" : "/passenger", { replace: true });
    } else {
      setIsOnline(user.is_online || false);
      if (user.online_since !== undefined) setOnlineSince(user.online_since);
      setConnectionStale(!!user.connection_stale);
      if (user.current_lat && user.current_lng) {
        setCurrentLoc({ name: "Current", lat: user.current_lat, lng: user.current_lng });
      }
      // First time we land on the page this session, tell the driver clearly
      // that they were auto-reset offline at login so they remember to toggle.
      if (!user.is_online && !sessionStorage.getItem("driver_offline_notice_shown")) {
        sessionStorage.setItem("driver_offline_notice_shown", "1");
        toast.warning("You're OFFLINE — toggle Online to start receiving ride offers.", { duration: 6000 });
      }
    }
  }, [user, loading, navigate]);

  // Live tick so the "Online for" label counts up without a page refresh.
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  // Reuse the existing fatigue endpoint as the single source for online time:
  // authoritative session start (online_since) + today's cumulative hours.
  useEffect(() => {
    if (!user || user.role !== "driver") return;
    let cancelled = false;
    const loadOnlineTime = async () => {
      try {
        const { data } = await api.get("/driver/fatigue");
        if (cancelled) return;
        setOnlineSince(data.online_since ?? null);
        setTodayOnlineHours(data?.hours?.online_hours_today ?? null);
      } catch { /* non-fatal, read-only display */ }
    };
    loadOnlineTime();
    const t = setInterval(loadOnlineTime, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);


  // Poll pending offer + active ride
  useEffect(() => {
    if (!user) return;
    fetchAll();
    pollRef.current = setInterval(fetchAll, 2500);
    return () => clearInterval(pollRef.current);
    // SAFE: fetchAll is recreated each render but only writes state via
    // stable React setters (setPendingOffer, setRides, setActiveRide,
    // setMyRating, setCycle). It never READS state, so the stale-closure
    // risk that exhaustive-deps protects against doesn't apply here.
    // Adding fetchAll would tear down + recreate the polling interval on
    // every render — a real regression for the iteration-55 offer-visibility
    // work since the 2.5s polling cadence would never settle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // === RECOVERY ON RETURN FROM BACKGROUND ===
  // Mobile browsers throttle / suspend backgrounded tabs aggressively. When
  // the driver leaves the app (Google Maps, a call, the home screen) and
  // comes back, the poll loop may have stalled for a long time and watchPosition
  // may have stopped firing. We hit /driver/my-rides immediately on tab
  // visibility/focus and request a one-shot GPS fix so the UI snaps back to
  // the truth before the driver has to look for it.
  useEffect(() => {
    if (!user) return;
    const resync = () => {
      fetchAll();
      // One-shot fresh fix — watchPosition can take 10–30s to wake back up.
      if (typeof navigator !== "undefined" && navigator.geolocation && isOnline) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude, longitude, accuracy } = pos.coords;
            setCurrentLoc({ name: "Live GPS", lat: latitude, lng: longitude });
            setGpsStatus({
              available: true,
              lastFixAt: Date.now(),
              accuracy: Math.round(accuracy),
              error: null,
              code: null,
            });
            api.post("/driver/location", { lat: latitude, lng: longitude })
              .then(() => {
                // Successful heartbeat clears the connection-stale banner
                // (backend also $unset the user doc on every heartbeat).
                setConnectionStale((s) => (s ? false : s));
              })
              .catch(() => {});
          },
          () => { /* leave gpsStatus as-is; watchPosition will recover */ },
          { enableHighAccuracy: true, maximumAge: 0, timeout: 8000 }
        );
      }
    };
    const onVis = () => { if (document.visibilityState === "visible") resync(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", resync);
    window.addEventListener("pageshow", resync);  // fires on bfcache restore
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", resync);
      window.removeEventListener("pageshow", resync);
    };
    // SAFE: same reasoning as the polling effect above — fetchAll only writes
    // state via stable setters; adding it would re-attach the visibility
    // listeners on every render and miss bfcache restore events.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isOnline]);

  // Stream location every 5 sec when online
  useEffect(() => {
    if (!user) return;
    if (isOnline) {
      locStreamRef.current = setInterval(() => {
        api.post("/driver/location", { lat: currentLoc.lat, lng: currentLoc.lng })
          .then(() => setLastHeartbeatAt(Date.now()))
          .catch(() => {});
      }, 5000);
    }
    return () => {
      if (locStreamRef.current) clearInterval(locStreamRef.current);
    };
  }, [isOnline, currentLoc, user]);

  // === REAL DEVICE GPS (navigator.geolocation.watchPosition) ===
  // Critical for real-world driver flow: without this, currentLoc stays at
  // the preset (Town Centre) or whatever the autocomplete last set, so the
  // 100m pickup geofence never triggers and CONFIRM ARRIVAL stays disabled
  // when the driver physically reaches the pickup. E2E suites mask this
  // with the "Teleport to pickup" simulator — production cannot.
  useEffect(() => {
    if (!user) return;
    // Tear down any prior watch when going offline or unmounting.
    const teardown = () => {
      if (gpsWatchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(gpsWatchIdRef.current);
        gpsWatchIdRef.current = null;
      }
    };
    if (!isOnline) {
      teardown();
      return;
    }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGpsStatus({
        available: false, lastFixAt: null, accuracy: null,
        error: "Geolocation API not supported by this browser",
        code: "unsupported",
      });
      return;
    }
    // Mark "attempting" so the UI can show a neutral spinner state
    // instead of jumping straight to red.
    setGpsStatus((s) => ({ ...s, error: null }));
    gpsWatchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, accuracy, heading, speed } = pos.coords;
        setCurrentLoc({ name: "Live GPS", lat: latitude, lng: longitude });
        // Keep the last finite heading — devices report null/NaN when stationary.
        if (Number.isFinite(heading)) setGpsHeading(heading);
        // GPS speed is m/s; convert to mph. null when unknown so the HUD shows "—".
        setGpsSpeedMph(Number.isFinite(speed) && speed >= 0 ? speed * 2.2369363 : null);
        setGpsStatus({
          available: true,
          lastFixAt: Date.now(),
          accuracy: Math.round(accuracy),
          error: null,
          code: null,
        });
        // Push immediately on each fix so the backend's view is fresh
        // (the 5s interval still runs as a heartbeat).
        api.post("/driver/location", { lat: latitude, lng: longitude })
          .then(() => setLastHeartbeatAt(Date.now()))
          .catch(() => {});
      },
      (err) => {
        // err.code: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
        const codeMap = { 1: "permission_denied", 2: "position_unavailable", 3: "timeout" };
        setGpsStatus((s) => ({
          available: false,
          lastFixAt: s.lastFixAt,  // preserve last good fix age, if any
          accuracy: s.accuracy,
          error: err.message || "Location error",
          code: codeMap[err.code] || "unknown",
        }));
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
    );
    return teardown;
  }, [user, isOnline]);

  const fetchAll = async () => {
    // CRITICAL: use Promise.allSettled, NOT Promise.all.
    // The old Promise.all swallowed *every* result if any sidecar request
    // (e.g. /driver/cycle or /me/rating) returned 4xx/5xx. That meant a
    // pending ride offer never reached setPendingOffer — the driver saw
    // an empty screen while the backend believed the offer was delivered.
    // With allSettled each promise is evaluated independently, so an
    // offer is rendered even if a peripheral endpoint fails.
    //
    // Iteration 56 (2026-05-30): also poll `/auth/me` so the UI catches
    // backend-side `is_online` flips (notably the janitor's auto-offline
    // sweep for drivers with stale `last_seen`). Without this the UI
    // happily renders "ONLINE" while /driver/pending-offer correctly
    // returns null because the backend has the driver as offline — the
    // exact "Sarah shows 'Offer sent to John' but John sees nothing"
    // repro on 2026-05-29.
    const [offerRes, mineRes, ratingRes, cycleRes, meRes] = await Promise.allSettled([
      api.get("/driver/pending-offer"),
      api.get("/driver/my-rides"),
      api.get("/me/rating"),
      api.get("/driver/cycle"),
      api.get("/auth/me"),
    ]);

    if (offerRes.status === "fulfilled") {
      const next = offerRes.value.data || null;
      // Defence-in-depth (iteration 57): reject any offer whose
      // `offered_to` does not match the currently authenticated user's
      // id. Backend already filters by `offered_to == self` server-side
      // (verified iteration-56 curl: Oni's /driver/pending-offer
      // returns null when ride.offered_to=John.id). The ONLY way the
      // wrong payload reaches this code path is shared localStorage
      // across two driver tabs in the same browser profile — the next
      // poll happens to use the OTHER driver's JWT for one request.
      // We refuse to render any offer that does not belong to us.
      if (next && next.offered_to && user && next.offered_to !== user.id) {
        // eslint-disable-next-line no-console
        console.warn(
          "[driver] REJECTED offer payload — ride.offered_to does not match logged-in user.",
          { rideOfferedTo: next.offered_to, currentUserId: user.id, rideId: next.id }
        );
        // Do not flip pendingOffer to this stranger's offer. If we
        // had a previous offer of our own, leave it; otherwise null.
        setPendingOffer((prev) => {
          if (prev && prev.id !== next.id) return prev;  // keep our own
          if (prev) clearPendingOffer("rejected-cross-driver-leak", prev.id);
          return null;
        });
      } else {
        // Offer-visibility observability (iteration 55) — log any
        // transition where the driver was seeing an offer and now isn't.
        // The backend [offer-visibility] log captures the SERVER-side
        // reason; this one captures what the UI actually rendered.
        setPendingOffer((prev) => {
          if (prev && !next) {
            clearPendingOffer("poll-returned-null", prev.id);
            return null;
          }
          return next;
        });
      }
    } else {
      // Only log; never let an offer-fetch failure block the rest of the
      // UI. Polling will retry every 2.5s. We DO NOT clear the existing
      // offer card on a transient failure — that would re-introduce the
      // "<10s flash" symptom the diagnostics are designed to catch.
      // eslint-disable-next-line no-console
      console.warn("[driver] /driver/pending-offer failed (offer NOT cleared):", offerRes.reason?.message);
    }

    if (mineRes.status === "fulfilled") {
      const list = mineRes.value.data || [];
      setRides(list);
      const active = list.find((r) => r.status === "accepted" || r.status === "in_progress");

      // Receipt auto-recovery: when the WS `ride_completed` event is
      // missed (WS disconnected, server restart, network blip), the
      // poll is our ONLY path to surface the driver receipt for an
      // admin force-completed ride. Detect "ride was active on the
      // previous render but is now completed AND we don't already
      // have a receipt for it" and populate lastCompleted from the
      // canonical /driver/my-rides record.
      setActiveRide((prev) => {
        if (prev && !active) {
          // ActiveRide is clearing this tick — was the prior ride
          // completed and is there no receipt yet?
          const completedSame = list.find(
            (r) => r.id === prev.id && r.status === "completed",
          );
          if (completedSame) {
            setLastCompleted((cur) => {
              if (cur && cur.id === completedSame.id) return cur;
              return {
                id: completedSame.id,
                passenger_name: completedSame.passenger_name,
                pickup_address: completedSame.pickup_address,
                dropoff_address: completedSame.dropoff_address,
                fare: completedSame.fare,
                payment_status: completedSame.payment_status || "pending",
                payment_method: completedSame.payment_method || "cash",
                breakdown: completedSame.fare_breakdown || completedSame.breakdown,
                completed_at: completedSame.completed_at || new Date().toISOString(),
                completed_by: completedSame.completed_by || "admin",
              };
            });
          } else {
            // Cancellation auto-recovery: the realtime `ride_cancelled`
            // frame can be missed when the driver's socket is
            // backgrounded/dropped (common while parked at the pickup
            // after arrival). The poll is then the ONLY path that tells
            // the driver their accepted ride was cancelled — raise the
            // same blocking modal + chime the WS handler would have.
            const cancelledSame = list.find(
              (r) => r.id === prev.id && r.status === "cancelled",
            );
            if (cancelledSame) {
              raiseCancelledModal(prev.id, cancelledSame.cancelled_by);
            }
          }
        }
        return active || null;
      });
    } else {
      // eslint-disable-next-line no-console
      console.warn("[driver] /driver/my-rides failed:", mineRes.reason?.message);
    }

    if (ratingRes.status === "fulfilled") {
      setMyRating(ratingRes.value.data);
    }
    // /driver/cycle failure is non-fatal — earnings strip just renders without numbers.
    if (cycleRes.status === "fulfilled") {
      setCycle(cycleRes.value.data || null);
    }
    // /auth/me reconciliation — single, idempotent point that catches the
    // backend → UI drift on is_online. Toggling LOCAL `isOnline` to match
    // the server is the smallest fix; it does NOT call /driver/online
    // (which would echo a no-op write back to the backend) — backend
    // already has the correct state.
    if (meRes.status === "fulfilled") {
      const backendOnline = !!meRes.value.data?.is_online;
      const offReason = meRes.value.data?.auto_offlined_reason;
      setIsOnline((local) => {
        if (local && !backendOnline) {
          // eslint-disable-next-line no-console
          console.warn(
            "[driver] backend says is_online=false but UI thought true — reconciling to OFFLINE.",
            { auto_offlined_at: meRes.value.data?.auto_offlined_at, reason: offReason }
          );
          toast.warning(
            offReason === "server_restart"
              ? "A system update took you offline. Tap Online to start receiving ride offers again."
              : "You're offline. Tap Online to start receiving ride offers.",
            { duration: 7000 }
          );
          return false;
        }
        return local;
      });
    }
  };
  // Keep the ref updated so the visibilitychange handler (defined
  // BEFORE fetchAll in the file order due to React hook rules) can
  // call the latest fetchAll closure.
  useEffect(() => {
    fetchAllRef.current = fetchAll;
  });


  // Single source of truth for offer-card clearing. Every path that wipes
  // `pendingOffer` MUST go through here so the console timeline tells the
  // operator *why* the card vanished. Reasons are an enumerated string;
  // keep them in sync with services/offer_visibility.py on the backend
  // so timelines stitch together cleanly.
  const clearPendingOffer = (reason, rideId) => {
    // eslint-disable-next-line no-console
    console.warn(
      `[offer-visibility] driver-ui cleared offer card · reason=${reason} · ride=${(rideId || "?").slice(0, 8)}`
    );
    setPendingOffer(null);
  };

  const toggleOnline = async (val) => {
    // Tap on the Online toggle is a user gesture — perfect place to unlock
    // the AudioContext (required by Chrome/Safari/Firefox autoplay policy).
    // Doing this here AND on the dedicated Sound button means the ringtone
    // is reliably ready by the time the first offer lands.
    unlockRingtone();
    try {
      await api.post("/driver/online", {
        is_online: val,
        lat: currentLoc.lat,
        lng: currentLoc.lng,
      });
      setIsOnline(val);
      // Optimistically mirror the backend's online_since so the live
      // "Online for" label appears instantly instead of waiting up to 60s
      // for the next /driver/fatigue poll to reconcile it. Read-only display
      // only — the authoritative value is still set_driver_online_state.
      setOnlineSince(val ? new Date().toISOString() : null);
      toast.success(val ? "You're now online" : "You're offline");
      refresh();
    } catch (e) {
      // Phase 1 launch-readiness: surface the structured detail reasons
      // (approval_pending / approval_rejected / account_suspended) so
      // a driver who somehow taps the toggle while ungated gets a
      // friendly toast instead of a generic 403 — even though the
      // banner above already explains the situation.
      const detail = e?.response?.data?.detail;
      const reason = detail?.reason;
      if (reason === "approval_pending" || reason === "approval_rejected" || reason === "account_suspended" || reason === "documents_noncompliant") {
        toast.error(detail?.message || "You can't go online yet.");
      } else if (e?.response?.status === 403 && typeof detail === "string" && /subscription/i.test(detail)) {
        // Subscriptions Phase 2 hard-gate — guide the driver to subscribe.
        toast.error(detail, {
          action: { label: "Subscribe", onClick: () => navigate("/driver/subscription") },
        });
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  const toggleRingtoneMute = () => {
    unlockRingtone();
    setRingtoneMuted((m) => {
      const next = !m;
      try {
        localStorage.setItem("driver_ringtone_muted", next ? "1" : "0");
      } catch { /* private-mode storage disabled — ignore */ }
      if (next) {
        toast.message("Sound muted — you won't hear ride offers");
      } else {
        // Turning sound back ON from a real tap: play an audible confirmation
        // so the driver knows alerts work, then confirm with a toast.
        testBeep();
        toast.success("Sound on — you'll hear ride offers, messages and alerts");
      }
      return next;
    });
  };

  const acceptOffer = async () => {
    if (!pendingOffer) return;
    const rid = pendingOffer.id;
    try {
      await api.post("/driver/accept", { ride_id: rid });
      toast.success("Ride accepted — heading to pickup");
      clearPendingOffer("accepted", rid);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const declineOffer = async () => {
    if (!pendingOffer) return;
    const rid = pendingOffer.id;
    try {
      await api.post("/driver/decline", { ride_id: rid });
      toast.info("Offer declined");
      clearPendingOffer("declined", rid);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const postArrival = async ({ manual = false } = {}) => {
    if (!activeRide) return;
    try {
      await api.post(`/driver/arrived/${activeRide.id}${manual ? "?manual=true" : ""}`);
      toast.success(manual ? "Arrival confirmed (manual)" : "Arrival confirmed");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmArrival = async ({ manual = false } = {}) => {
    if (!activeRide) return;
    // When the driver is NOT within the 100m geofence (or GPS is blocked),
    // require explicit confirmation so they don't tap-through accidentally.
    // We use an in-app dialog rather than window.confirm() because native
    // confirm() is suppressed in iOS standalone PWAs / in-app webviews — that
    // silently dropped the tap and left the ride stuck (the dispatch-booking
    // manual-arrival bug). The in-app dialog works everywhere.
    if (manual && !isWithinPickupGeofence) {
      setManualArrivePromptOpen(true);
      return;
    }
    await postArrival({ manual });
  };

  const startTrip = async () => {
    if (!activeRide) return;
    try {
      await api.post(`/driver/start/${activeRide.id}`);
      toast.success("Trip started");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Multi-stop journeys (#8) — advance the journey to the next waypoint.
  // Additive progress counter only; does NOT touch the ride lifecycle.
  const advanceStop = async () => {
    if (!activeRide) return;
    try {
      const { data } = await api.post(`/driver/advance-stop/${activeRide.id}`);
      toast.success(
        data?.all_stops_done
          ? "All stops done — continue to the final drop-off"
          : "Stop reached — navigating to the next stop",
      );
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const completeTrip = async ({ manual = false } = {}) => {
    if (!activeRide) return;

    // Geofence-validated completion. Server now enforces:
    //   0–100m   → allow
    //   100–500m → allow with override_reason
    //   >500m    → block (admin force-complete only)
    //   no GPS   → block
    // Frontend must POST current GPS in the body so the server can audit.
    // Server-state recheck — defensive guard against iOS Safari
    // suspending the tab while admin force-completes the ride. When the
    // driver foregrounds the app, their local activeRide may be stale
    // from before the suspension. If we don't recheck, the client-side
    // geofence check below fires from stale state and the driver gets
    // the "you're 2392m from drop-off" error on a ride that no longer
    // exists. Production root cause confirmed 2026-06-07.
    try {
      const { data: liveRides } = await api.get("/driver/my-rides");
      const stillActive = (liveRides || []).find(
        (r) => r.id === activeRide.id && (r.status === "accepted" || r.status === "in_progress"),
      );
      if (!stillActive) {
        // Ride has been completed/cancelled server-side (likely admin
        // force-complete while tab was backgrounded). Recover the
        // receipt locally instead of confusing the driver.
        const completedRide = (liveRides || []).find(
          (r) => r.id === activeRide.id && r.status === "completed",
        );
        if (completedRide) {
          setLastCompleted({
            id: completedRide.id,
            passenger_name: completedRide.passenger_name,
            pickup_address: completedRide.pickup_address,
            dropoff_address: completedRide.dropoff_address,
            fare: completedRide.fare,
            payment_status: completedRide.payment_status || "pending",
            payment_method: completedRide.payment_method || "cash",
            breakdown: completedRide.fare_breakdown || completedRide.breakdown,
            completed_at: completedRide.completed_at || new Date().toISOString(),
            completed_by: completedRide.completed_by || "admin",
          });
          toast.message("Ride was completed by dispatch", {
            description: (completedRide.payment_method || "cash") === "cash"
              ? "Collect cash from passenger and tap Got It."
              : "Passenger will pay by card.",
          });
        } else {
          toast.message("This ride is no longer active. Refreshing…");
        }
        setActiveRide(null);
        setPendingOffer(null);
        return;
      }
    } catch {
      // Network blip — fall through to the existing client-side checks.
    }

    // Use the SAME GPS source that drives the LIVE GPS badge and the
    // distance-to-drop-off readout. Earlier this function read from a
    // separate `location` variable that could be null even when the
    // badge said "LIVE GPS +61m" — producing the impossible toast
    // "Enable location services to end the trip." while live GPS was
    // visibly tracking. Regression: see test "live-gps-visible-no-services-error".
    let driverGps = (currentLoc && currentLoc.lat != null && currentLoc.lng != null)
      ? { lat: currentLoc.lat, lng: currentLoc.lng }
      : null;
    // Staleness guard — if the badge is dark / coordinates are the
    // zero-island fallback, treat as missing rather than mislocating
    // the driver in the middle of the Atlantic.
    if (driverGps && driverGps.lat === 0 && driverGps.lng === 0) driverGps = null;
    if (!driverGps) {
      toast.error("GPS unavailable — wait for the LIVE GPS badge before ending the trip.");
      return;
    }

    // Compute distance the same way the UI banner does, so the dialog
    // matches what the driver is seeing on screen.
    const distM = distanceToDropoffKm != null && isFinite(distanceToDropoffKm)
      ? Math.round(distanceToDropoffKm * 1000)
      : null;

    let override_reason = "";
    if (distM != null && distM > 500) {
      toast.error(`You're ${distM}m from the drop-off. Completion is blocked beyond 500m — call dispatch for a force-complete.`);
      return;
    }
    if (distM != null && distM > 100) {
      // 100–500m band — require an explicit reason.
      const reason = window.prompt(
        `You're ${distM}m from the drop-off.\n\n` +
        `Why are you completing here? (e.g. "passenger asked to stop early", "GPS drift in carpark")\n\n` +
        `This reason will be logged for the dispatcher.`
      );
      if (!reason || !reason.trim()) {
        toast.error("Override reason required to complete outside 100m.");
        return;
      }
      override_reason = reason.trim();
    } else if (manual) {
      // Manual-button path with unknown distance (GPS fix not yet acquired).
      // Block rather than show a soft confirm — the production geofence
      // guard requires GPS anyway, so a "completion uncertain" tap would
      // 422 with geofence_gps_required. Prevent the round-trip.
      toast.error(
        "Waiting for GPS lock. Try again in a few seconds, or tap " +
        "Navigate to refresh location."
      );
      return;
    }

    try {
      const body = {
        driver_lat: driverGps.lat,
        driver_lng: driverGps.lng,
        override_reason,
      };
      const { data } = await api.post(
        `/driver/complete/${activeRide.id}${manual ? "?manual=true" : ""}`,
        body,
      );
      toast.success(`Trip complete · £${data.fare}`);
      // Capture the breakdown + key context so the dismissible
      // post-completion card has everything to render even after activeRide
      // becomes null on next fetchAll().
      setLastCompleted({
        id: activeRide.id,
        passenger_name: activeRide.passenger_name,
        pickup_address: activeRide.pickup_address,
        dropoff_address: activeRide.dropoff_address,
        fare: data.fare,
        payment_status: data.payment_status,
        payment_method: activeRide.payment_method,
        breakdown: data.breakdown,
        completed_at: new Date().toISOString(),
      });
      // Reset the trip view so the next ride starts on NAV.
      setTripViewPersisted("nav");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Driver confirms cash-in-hand for a just-completed cash ride. Flips
  // the lastCompleted receipt's payment status from PENDING → PAID and
  // calls the backend so the ride document + audit trail are updated.
  const markCashCollected = async () => {
    if (!lastCompleted?.id) return;
    try {
      const { data } = await api.post(`/driver/mark-paid/${lastCompleted.id}`);
      setLastCompleted((prev) => prev ? { ...prev, payment_status: data.payment_status || "paid" } : prev);
      toast.success("Cash collected — receipt updated");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Reset manual arrival overrides when the ride phase changes. Otherwise a
  // "manual arrived" tap on a previous ride leg would leak into the next.
  useEffect(() => {
    setManualArrivedPickup(false);
    setManualArrivedDropoff(false);
    if (activeRide?.id !== nearbyNotifiedRef.current) {
      nearbyNotifiedRef.current = null;
    }
  }, [activeRide?.id, activeRide?.phase]);

  // Distance + local proximity flags. These DO NOT advance backend state —
  // they only enable the explicit "Confirm Arrival" / "End Trip" buttons
  // (so the driver can't accidentally tap them from miles away).
  const distanceToPickupKm = activeRide
    ? haversineKm(currentLoc.lat, currentLoc.lng, activeRide.pickup_lat, activeRide.pickup_lng)
    : null;
  const distanceToDropoffKm = activeRide
    ? haversineKm(currentLoc.lat, currentLoc.lng, activeRide.dropoff_lat, activeRide.dropoff_lng)
    : null;

  // PRE-ARRIVAL HEADS-UP: 500m geofence on the pickup leg. Fires once per
  // ride and only when phase is still ASSIGNED / DRIVER_EN_ROUTE (the legs
  // where the passenger isn't yet expecting the driver).
  useEffect(() => {
    if (!activeRide) return;
    const isPickupLeg =
      activeRide.phase === "assigned" || activeRide.phase === "driver_en_route";
    if (
      isPickupLeg &&
      nearbyNotifiedRef.current !== activeRide.id &&
      distanceToPickupKm !== null &&
      isFinite(distanceToPickupKm) &&
      distanceToPickupKm <= NEARBY_THRESHOLD_KM
    ) {
      nearbyNotifiedRef.current = activeRide.id;
      api.post(`/driver/nearby/${activeRide.id}`).catch(() => { /* best-effort */ });
    }
    // TODO Phase 2: scanner wants `activeRide` here, but the effect only
    // reads `.id` and `.phase` which ARE explicit deps. Out of scope for the
    // iteration-55 surgical hardening (proximity notify path).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRide?.id, activeRide?.phase, distanceToPickupKm]);

  // Phase-driven helpers: derive what the driver is actually doing right now
  // from the BACKEND phase, not local flags. Local proximity (auto / manual)
  // only enables the explicit Confirm-Arrival / End-Trip buttons — it never
  // unilaterally pretends the ride has transitioned.
  const phase = activeRide?.phase;
  const isPickupLeg =
    phase === "assigned" || phase === "driver_en_route";
  const isAtPickup = phase === "driver_arrived_pickup";
  // The driver has physically reached the drop-off (backend auto-arrival
  // geofence flipped phase → driver_arrived_dropoff while status stays
  // in_progress). This is still the DROP-OFF leg: NAVIGATE targets the
  // destination and the primary action must be END TRIP.
  const isAtDropoff = phase === "driver_arrived_dropoff";
  const isInTransit = phase === "trip_in_progress" || isAtDropoff;
  const isWithinPickupGeofence =
    isPickupLeg && distanceToPickupKm !== null && distanceToPickupKm <= ARRIVAL_THRESHOLD_KM;
  const isWithinDropoffGeofence =
    isInTransit && distanceToDropoffKm !== null && distanceToDropoffKm <= ARRIVAL_THRESHOLD_KM;
  const canConfirmArrival = isPickupLeg && (isWithinPickupGeofence || manualArrivedPickup);
  // Enable END TRIP once within the drop-off geofence, on manual confirm, OR
  // when the backend has already confirmed drop-off arrival (driver_arrived_dropoff).
  const canEndTrip = isInTransit && (isWithinDropoffGeofence || manualArrivedDropoff || isAtDropoff);

  // One-shot toast + haptic when the driver first crosses the 100m geofence,
  // nudging them to tap the explicit CTA. Fires once per ride/leg pair.
  useEffect(() => {
    if (!activeRide) return;
    const legKey = canConfirmArrival ? "pickup" : canEndTrip ? "dropoff" : null;
    if (!legKey) return;
    if (
      lastArrivalToastRef.current.ride === activeRide.id &&
      lastArrivalToastRef.current.phase === legKey
    ) {
      return;
    }
    lastArrivalToastRef.current = { ride: activeRide.id, phase: legKey };
    toast.success(
      legKey === "pickup"
        ? "You're at the pickup — tap CONFIRM ARRIVAL"
        : "You're at the drop-off — tap END TRIP"
    );
    if (navigator.vibrate) navigator.vibrate([180, 90, 180]);
    // TODO Phase 2: same as the proximity-notify effect above — `activeRide`
    // is only read via `.id` which is in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRide?.id, canConfirmArrival, canEndTrip]);

  // Build map markers
  // Reset directions polyline whenever the active ride ends — prevents
  // stale routes flashing on the map between rides.
  useEffect(() => {
    if (!activeRide) setDirectionsRoute(null);
  }, [activeRide]);

  // Auto-close the in-app full-screen navigation whenever the trip is no longer
  // in progress (completed/cancelled/handed back), so the driver isn't stranded
  // on a navigation map for a ride that has ended.
  useEffect(() => {
    if (!(isInTransit && activeRide) && navMapOpen) setNavMapOpen(false);
    if (!(isInTransit && activeRide) && navInfo) setNavInfo(null);
  }, [isInTransit, activeRide, navMapOpen, navInfo]);

  // Close chat + clear incoming-message buffer whenever the active ride
  // changes (ride completed, cancelled, or a new ride begins). The
  // backend already returns an empty thread for non-active rides, but
  // tearing down the dialog here also drops any stale toast cadence.
  useEffect(() => {
    setChatOpen(false);
    setIncomingMessage(null);
  }, [activeRide?.id]);


  const markers = [
    { id: "me", lat: currentLoc.lat, lng: currentLoc.lng, type: "driver", label: "YOU" },
  ];
  if (activeRide) {
    markers.push(
      { id: "p", lat: activeRide.pickup_lat, lng: activeRide.pickup_lng, type: "pickup", label: "PICKUP" },
      { id: "d", lat: activeRide.dropoff_lat, lng: activeRide.dropoff_lng, type: "dropoff", label: "DROPOFF" }
    );
  } else if (pendingOffer) {
    markers.push(
      { id: "p", lat: pendingOffer.pickup_lat, lng: pendingOffer.pickup_lng, type: "pickup", label: "PICKUP" },
      { id: "d", lat: pendingOffer.dropoff_lat, lng: pendingOffer.dropoff_lng, type: "dropoff", label: "DROPOFF" }
    );
  }

  const completedRides = rides.filter((r) => r.status === "completed");

  const isApproved = user && user.role === "driver" && user.approval_status === "approved";

  // SOS trigger sized for the cockpit's top-right slot (compact glass FAB).
  const cockpitSosButton = (
    <SosButton
      incomingSignal={sosMsgSignal}
      triggerClassName="w-12 h-12 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white shadow-2xl flex flex-col items-center justify-center select-none active:scale-95 transition-transform border-2 border-white/40"
    />
  );

  // Floating glass status nudges (offline / stale connection). Note: the old
  // "Enable sound" prompt was removed — audio is ON by default and unlocked
  // silently on first interaction; the driver controls it via the speaker toggle.
  const cockpitBanners = (
    <>
      <DocumentExpiryBanner refreshKey={cockpitTab} onRenew={(t) => { setRenewDocType({ type: t, n: Date.now() }); setCockpitTab("account"); }} />
      {connectionStale && isOnline && (
        <div
          className="rounded-2xl bg-[#7C2D12]/90 backdrop-blur-xl border border-[#F59E0B]/40 px-4 py-3 text-white shadow-lg"
          data-testid="driver-connection-stale-banner"
        >
          <div className="font-bold text-sm leading-tight">Connection stale — keep this screen open</div>
          <div className="text-xs opacity-90 mt-0.5">
            Re-enabling GPS or tapping the map clears this. You're still online and dispatch-eligible.
          </div>
        </div>
      )}
    </>
  );

  const activeRideCardEl = activeRide ? (
    <ActiveRideCard
      activeRide={activeRide}
      phase={phase}
      isPickupLeg={isPickupLeg}
      isAtPickup={isAtPickup}
      isInTransit={isInTransit}
      canConfirmArrival={canConfirmArrival}
      canEndTrip={canEndTrip}
      distanceToPickupKm={distanceToPickupKm}
      distanceToDropoffKm={distanceToDropoffKm}
      isOnline={isOnline}
      gpsStatus={gpsStatus}
      tripView={tripView}
      setTripViewPersisted={setTripViewPersisted}
      navApp={navApp}
      setChatOpen={setChatOpen}
      incomingMessage={incomingMessage}
      setDirectionsRoute={setDirectionsRoute}
      setNavInfo={setNavInfo}
      setCurrentLoc={setCurrentLoc}
      directionsRefreshSignal={rerouteSignal}
      confirmArrival={confirmArrival}
      startTrip={startTrip}
      completeTrip={completeTrip}
      advanceStop={advanceStop}
    />
  ) : null;

  const completedCardEl = !activeRide && lastCompleted ? (
    <CompletedRideCard
      lastCompleted={lastCompleted}
      dismiss={() => setLastCompleted(null)}
      markCashCollected={markCashCollected}
    />
  ) : null;

  // Location + navigation-app controls (demo GPS + Google/Waze picker) live in
  // the Account tab so nothing is removed from the driver's reach.
  const locationControls = (
    <div className="bg-white border border-border p-4" data-testid="driver-location-controls">
      <div className="label-mini text-muted-foreground mb-2">CURRENT POSITION</div>
      <div className="border border-border bg-white mb-2">
        <PlaceAutocomplete
          value={currentLoc}
          onChange={(p) => {
            setCurrentLoc(p);
            api.post("/driver/location", { lat: p.lat, lng: p.lng });
          }}
          placeholder="Search any address…"
          testId="driver-loc-autocomplete"
        />
      </div>
      <div className="flex flex-wrap gap-1">
        {PRESET_DRIVER_LOCATIONS.map((p) => (
          <button
            key={p.name}
            onClick={() => {
              setCurrentLoc(p);
              api.post("/driver/location", { lat: p.lat, lng: p.lng });
            }}
            className={`text-xs px-2 py-1 border transition-colors ${
              currentLoc.name === p.name ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-border hover:bg-secondary"
            }`}
            data-testid={`driver-loc-${p.name.replace(/\s/g, "")}`}
          >
            {p.name}
          </button>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-border">
        <div className="label-mini text-muted-foreground mb-1.5">NAVIGATE WITH</div>
        <div className="flex gap-1" data-testid="nav-app-toggle">
          <button
            type="button"
            onClick={() => selectNavApp("google")}
            className={`flex-1 text-xs font-bold px-3 py-1.5 border transition-colors ${
              navApp === "google" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-border hover:bg-secondary"
            }`}
            data-testid="nav-app-google"
          >
            Google Maps
          </button>
          <button
            type="button"
            onClick={() => selectNavApp("waze")}
            className={`flex-1 text-xs font-bold px-3 py-1.5 border transition-colors ${
              navApp === "waze" ? "bg-[#33CCFF] text-black border-[#33CCFF]" : "border-border hover:bg-secondary"
            }`}
            data-testid="nav-app-waze"
          >
            Waze
          </button>
        </div>
      </div>
    </div>
  );

  const tabSheet = cockpitTab !== "home" ? (
    <div className="fixed inset-0 z-[70] bg-[#0A0A0A] flex flex-col" data-testid={`cockpit-tab-${cockpitTab}`}>
      <div className="flex items-center justify-between px-4 py-4 border-b border-white/10 bg-[#0A0A0A]">
        <h2 className="font-heading font-black text-xl text-white capitalize">{cockpitTab}</h2>
        <button
          type="button"
          onClick={() => setCockpitTab("home")}
          data-testid="cockpit-tab-close"
          className="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center active:scale-95 transition-transform"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#F3F4F6]">
        {cockpitTab === "earnings" && (
          <DriverEarningsGate>
            <EarningsTodayLink />
            <MembershipLink />
            <DriverDashboardPanel />
            {myRating?.avg_rating && (
              <div className="flex items-center gap-2 text-sm bg-white border border-border px-4 py-3" data-testid="driver-rating-strip">
                <Star className="w-4 h-4 fill-[#FFC107] text-[#FFC107]" />
                <span className="font-bold">{myRating.avg_rating}</span>
                <span className="text-xs text-muted-foreground">({myRating.rating_count} ratings)</span>
              </div>
            )}
            {cycle?.history && cycle.history.length > 1 && (
              <div className="bg-white border border-border p-4" data-testid="cycle-history-section">
                <div className="label-mini text-muted-foreground mb-3">EARNINGS HISTORY (LAST 7 DAYS)</div>
                <div className="space-y-1.5">
                  {cycle.history.slice(0, 7).map((c) => (
                    <div key={c.cycle_date} className="flex items-center justify-between border border-border px-3 py-2 text-xs font-mono-tab" data-testid={`cycle-history-${c.cycle_date}`}>
                      <span className="text-muted-foreground">{c.cycle_date}</span>
                      <div className="flex items-center gap-3">
                        <span>£{(c.gross_earnings ?? 0).toFixed(2)}</span>
                        <span className="text-[#EF4444]">−£{(c.fee_amount ?? 0).toFixed(2)}</span>
                        <span className={`font-bold ${(c.net_earnings ?? 0) >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                          £{(c.net_earnings ?? 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DriverEarningsGate>
        )}
        {cockpitTab === "trips" && <RecentTripsList completedRides={completedRides} />}
        {cockpitTab === "account" && (
          <>
            <div className="bg-white border border-border p-4" data-testid="account-identity">
              <div className="label-mini text-muted-foreground mb-1">SIGNED IN AS</div>
              <div className="font-heading font-black text-lg" data-testid="driver-identity-name">{user?.name || "—"}</div>
              <div className="text-xs text-muted-foreground font-mono" data-testid="driver-identity-email">{user?.email}</div>
              <button
                type="button"
                onClick={async () => {
                  const res = await (typeof logout === "function" ? logout() : Promise.resolve({ blocked: false }));
                  if (!res?.blocked) navigate("/login");
                }}
                className="mt-3 text-xs font-bold uppercase tracking-wider text-white bg-[#0A0A0A] hover:bg-[#222] px-4 py-2 transition-colors"
                data-testid="driver-identity-signout"
              >
                Sign out
              </button>
            </div>
            {locationControls}
            <DriverPinSettings />
            <DriverPaymentPanel />
            <DriverCommPrefs />
            <DriverEmergencyContacts />
            <DriverDocumentsPanel renewDocType={renewDocType} />
            <div className="text-center text-[10px] text-muted-foreground/70 tracking-wider pt-2" data-testid="driver-build-tag">
              BUILD {BUILD_TAG}
            </div>
          </>
        )}
      </div>
    </div>
  ) : null;

  const cockpitScreen = (
    <div className="fixed inset-0 bg-[#0A0A0A]" data-testid="driver-cockpit-screen">
      <div className="absolute inset-0" data-testid="driver-map-section">
        <SmartMap
          markers={markers}
          routes={directionsRoute && directionsRoute.length > 1
            ? [{ points: directionsRoute, color: "#002FA7", weight: 5, opacity: 0.85 }]
            : null}
          className="absolute inset-0"
        />
      </div>
      <DriverCockpit
        isOnline={isOnline}
        onlineForLabel={isOnline && onlineSince ? fmtOnlineSpan(nowTick - new Date(onlineSince).getTime()) : null}
        todayOnlineLabel={fmtDecimalHours(todayOnlineHours)}
        activeRide={activeRide}
        pendingOffer={pendingOffer}
        cycle={cycle}
        myRating={myRating}
        completedCount={completedRides.length}
        ringtoneMuted={ringtoneMuted}
        driverName={user?.name}
        onToggleOnline={toggleOnline}
        onAccept={acceptOffer}
        onDecline={declineOffer}
        onToggleRingtone={toggleRingtoneMute}
        onNav={setCockpitTab}
        activeTab={cockpitTab}
        sosSlot={cockpitSosButton}
        activeRideSlot={activeRideCardEl}
        completedSlot={completedCardEl}
        banners={cockpitBanners}
      />
      {tabSheet}
    </div>
  );

  const onboardingScreen = (
    <div className="min-h-screen bg-background" data-testid="driver-onboarding-screen">
      <Header title="DRIVER" accent="yellow" />
      {user && user.role === "driver" && user.approval_status === "pending" && (
        <div className="bg-[#FFFBEB] border-b-2 border-[#F59E0B] px-4 md:px-6 py-3 flex items-start gap-3" data-testid="driver-approval-pending-banner">
          <div className="w-8 h-8 rounded-full bg-[#F59E0B] flex items-center justify-center flex-shrink-0">
            <Clock className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-black text-sm leading-tight text-[#92400E]">Application under review</div>
            <div className="text-xs text-[#92400E] mt-0.5 opacity-90">
              You'll be notified by email as soon as an operator approves your driver application. Until then, going online and accepting rides is disabled.
            </div>
          </div>
        </div>
      )}
      {user && user.role === "driver" && user.approval_status === "rejected" && (
        <div className="bg-[#FEF2F2] border-b-2 border-[#EF4444] px-4 md:px-6 py-3 flex items-start gap-3" data-testid="driver-approval-rejected-banner">
          <div className="w-8 h-8 rounded-full bg-[#EF4444] flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-black text-sm leading-tight text-[#991B1B]">Application not approved</div>
            <div className="text-xs text-[#991B1B] mt-0.5 opacity-90">
              {user.rejection_reason || "Contact dispatch support to discuss next steps."}
            </div>
          </div>
        </div>
      )}
      {user && user.role === "driver" && user.suspended && (
        <div className="bg-[#FEF2F2] border-b-2 border-[#0A0A0A] px-4 md:px-6 py-3 flex items-start gap-3" data-testid="driver-suspended-banner">
          <div className="w-8 h-8 rounded-full bg-[#0A0A0A] flex items-center justify-center flex-shrink-0">
            <Ban className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-black text-sm leading-tight">Account suspended</div>
            <div className="text-xs mt-0.5 opacity-80">
              Your driver account has been suspended by dispatch. Contact support to discuss reinstatement.
            </div>
            {user.suspension_reason && (
              <div className="text-xs mt-2 px-2 py-1 bg-white border border-[#FCA5A5] text-[#7F1D1D]" data-testid="driver-suspended-reason">
                <strong>Reason:</strong> {user.suspension_reason}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-6 space-y-4">
        <DriverOnboardingChecklist />
        <DriverDocumentsPanel />
        <DriverPaymentPanel />
        <DriverCommPrefs />
        <DriverEmergencyContacts />
        <div className="text-center text-[10px] text-muted-foreground/70 tracking-wider pt-2" data-testid="driver-build-tag">
          BUILD {BUILD_TAG}
        </div>
      </div>
    </div>
  );

  return (
    <DriverFinancialPrivacyProvider>
      {/* Full-screen ride-cancellation acknowledgement — blocks the UI until ack. */}
      {cancelledNotice && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" data-testid="driver-ride-cancelled-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 border-4 border-[#DC2626] shadow-2xl">
            <div className="w-14 h-14 rounded-full bg-[#FEE2E2] flex items-center justify-center mx-auto mb-4">
              <Ban className="w-7 h-7 text-[#DC2626]" />
            </div>
            <h2 className="font-heading font-black text-xl text-center text-[#111] mb-2" data-testid="driver-ride-cancelled-title">
              Ride cancelled
            </h2>
            <p className="text-center text-sm text-[#444] mb-6" data-testid="driver-ride-cancelled-message">
              {cancelledNotice.by === "admin"
                ? "Dispatch cancelled this ride. You are now available for new offers."
                : "Passenger cancelled this ride. You are now available for new offers."}
            </p>
            <Button
              className="w-full bg-[#111] text-white font-heading font-black h-12 rounded-xl hover:bg-[#222]"
              onClick={() => { setCancelledNotice(null); fetchAll(); }}
              data-testid="driver-ride-cancelled-ack-btn"
            >
              Got It
            </Button>
          </div>
        </div>
      )}

      {isApproved ? cockpitScreen : onboardingScreen}

      {/* Ride-scoped chat — page-root overlay. */}
      {activeRide && user && (
        <RideChatDialog
          open={chatOpen}
          onOpenChange={setChatOpen}
          rideId={activeRide.id}
          selfId={user.id}
          selfRole="driver"
          counterpartyName={activeRide.passenger_name}
          incomingMessage={incomingMessage}
        />
      )}

      {/* Manual-arrival confirmation dialog (PWA-safe, replaces window.confirm). */}
      <AlertDialog open={manualArrivePromptOpen} onOpenChange={setManualArrivePromptOpen}>
        <AlertDialogContent data-testid="manual-arrival-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm you're at the pickup?</AlertDialogTitle>
            <AlertDialogDescription>
              {distanceToPickupKm !== null && isFinite(distanceToPickupKm)
                ? `You're showing ${Math.round(distanceToPickupKm * 1000)}m from pickup. `
                : "GPS is unavailable. "}
              Manual arrivals are logged for the dispatcher.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="manual-arrival-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="manual-arrival-confirm-btn"
              onClick={() => { setManualArrivePromptOpen(false); postArrival({ manual: true }); }}
            >
              Yes, I'm at pickup
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* In-app full-screen live navigation map with the LIVE fare overlaid. */}
      {activeRide && (
        <NavigationFullScreen
          open={navMapOpen}
          onClose={() => setNavMapOpen(false)}
          markers={markers}
          route={directionsRoute}
          rideId={activeRide.id}
          startedAt={activeRide.started_at}
          fallbackEstimate={activeRide.estimated_fare}
          destLabel={activeRide.dropoff_address}
          nextStep={navInfo?.nextStep}
          steps={navInfo?.steps}
          etaText={navInfo?.durationText}
          gpsHeading={gpsHeading}
          gpsSpeedMph={gpsSpeedMph}
          gpsAccuracy={gpsStatus.accuracy}
          lastFixAt={gpsStatus.lastFixAt}
          lastHeartbeatAt={lastHeartbeatAt}
          etaSeconds={navInfo?.durationSeconds}
          remainingMeters={navInfo?.distanceMeters}
          onReroute={() => setRerouteSignal((n) => n + 1)}
          phoneBooking={activeRide?.booked_by_dispatch === true}
          distanceText={
            navInfo?.distanceText
              ? navInfo.distanceText
              : distanceToDropoffKm != null && isFinite(distanceToDropoffKm)
              ? distanceToDropoffKm >= 1
                ? `${distanceToDropoffKm.toFixed(1)} km`
                : `${Math.round(distanceToDropoffKm * 1000)} m`
              : "—"
          }
          navUrl={
            navApp === "waze"
              ? `https://www.waze.com/ul?ll=${activeRide.dropoff_lat}%2C${activeRide.dropoff_lng}&navigate=yes`
              : `https://www.google.com/maps/dir/?api=1&destination=${activeRide.dropoff_lat},${activeRide.dropoff_lng}&travelmode=driving`
          }
          navAppLabel={navApp === "waze" ? "Waze" : "Google Maps"}
        />
      )}
    </DriverFinancialPrivacyProvider>
  );
}
